// Charts, drawn to the same rules as the FY sales report: thin marks, 4px
// rounded data-ends, a 2px surface gap between stacked segments, hairline
// grid, a legend whenever there are two or more series, and a tooltip on
// every mark. No charting library — the shapes are simple and the palette is
// already validated for colour-blind separation in both themes.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { short, pct, monthLabel, slotColour } from '../lib/fmt';

/** Horizontal bar: rounded at the data end, square at the baseline. */
function hBar(x: number, y: number, w: number, h: number, r = 4) {
  if (w <= 0.6) return `M${x} ${y} h0.6 v${h} h-0.6 Z`;
  const rr = Math.min(r, w, h / 2);
  return `M${x} ${y} h${w - rr} a${rr} ${rr} 0 0 1 ${rr} ${rr} v${h - 2 * rr} a${rr} ${rr} 0 0 1 ${-rr} ${rr} h${-(w - rr)} Z`;
}
/** Vertical bar: rounded at the top, square at the baseline. */
function vBar(x: number, y: number, w: number, h: number, r = 4) {
  if (h <= 0.6) return `M${x} ${y} h${w} v0.6 h${-w} Z`;
  const rr = Math.min(r, w / 2, h);
  return `M${x} ${y + rr} a${rr} ${rr} 0 0 1 ${rr} ${-rr} h${w - 2 * rr} a${rr} ${rr} 0 0 1 ${rr} ${rr} v${h - rr} h${-w} Z`;
}

/**
 * The chart's own width in real pixels.
 *
 * These charts are drawn in absolute coordinates and then scaled to fit by the
 * viewBox. On a phone that scale is about 0.45, which renders 12.5px labels at
 * under 6px — present, but unreadable. Measuring instead lets the chart be
 * drawn at the size it will actually occupy, so a label is the size it says.
 */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next) setW(next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

interface Tip { x: number; y: number; head: string; rows: [string, string, string?][]; foot?: [string, string] }

function Tooltip({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return (
    <div className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full
                    bg-card border border-rule rounded shadow-lg px-3 py-2.5 min-w-[180px]"
         style={{ left: tip.x, top: tip.y - 12 }} role="status">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.09em] text-muted
                      pb-1.5 mb-2 border-b border-rule-soft">{tip.head}</div>
      {tip.rows.map(([name, value, colour], i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-[2.5px] text-xs text-ink-2">
          <span className="flex items-center gap-2 min-w-0">
            {colour && <i className="w-3 h-[2.5px] rounded shrink-0" style={{ background: colour }} />}
            <span className="truncate">{name}</span>
          </span>
          {/* Value leads, label follows: here the reader has the series and wants the number. */}
          <b className="font-semibold text-ink tnum whitespace-nowrap">{value}</b>
        </div>
      ))}
      {tip.foot && (
        <div className="flex justify-between gap-3 text-xs text-muted mt-2 pt-1.5 border-t border-rule-soft">
          <span>{tip.foot[0]}</span><b className="text-ink font-semibold tnum">{tip.foot[1]}</b>
        </div>
      )}
    </div>
  );
}

/**
 * Ranked horizontal bars. One series → one colour, because this compares
 * magnitude rather than identity; every bar is direct-labelled, so no value
 * axis is needed either.
 */
export function RankedBars(
  { rows, total, labelWidth = 210, colour = 'var(--color-s1)', detail }:
  { rows: { name: string; amount: number; colour?: string }[]; total?: number;
    labelWidth?: number; colour?: string;
    detail?: (r: { name: string; amount: number }) => [string, string, string?][] },
) {
  const [tip, setTip] = useState<Tip | null>(null);
  const [box, cw] = useWidth<HTMLDivElement>();

  // Drawn at the width it will actually occupy, so nothing is scaled down. Too
  // narrow for a label column beside the bars — a phone — and the name moves
  // above its bar instead of being squeezed or clipped.
  const W = cw > 0 ? Math.round(cw) : 760;
  const narrow = W < 520;
  const lblW = narrow ? 0 : labelWidth;
  const valueWidth = narrow ? 78 : 122;
  const rowH = narrow ? 52 : 38;
  const barH = 17;

  if (!rows.length) {
    return <div className="px-5 py-9 text-center text-[13.5px] text-muted">Nothing to show yet.</div>;
  }

  const H = rows.length * rowH + 12;
  const max = Math.max(...rows.map((r) => Math.abs(r.amount)), 1);
  const scale = Math.max(1, W - lblW - valueWidth) / max;
  const sum = total ?? rows.reduce((a, b) => a + b.amount, 0);

  return (
    <div className="relative" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} className="w-full block overflow-visible" role="img">
        {!narrow && (
          <line x1={lblW} y1={4} x2={lblW} y2={rows.length * rowH + 2}
                stroke="var(--color-baseline)" strokeWidth={1} />
        )}
        {rows.map((r, i) => {
          const y = i * rowH + 4;
          const by = narrow ? y + 22 : y + (rowH - barH) / 2 - 2;
          const w = Math.max(Math.abs(r.amount) * scale, 1.2);
          const c = r.colour ?? colour;
          return (
            <g key={r.name}>
              {narrow ? (
                <text x={0} y={y + 12} className="fill-ink text-[12.5px] font-medium">{r.name}</text>
              ) : (
                <text x={lblW - 13} y={by + barH / 2 + 4} textAnchor="end"
                      className="fill-ink text-[12.5px] font-medium">{r.name}</text>
              )}
              <path d={hBar(lblW, by, w, barH)} fill={c} />
              <text x={lblW + w + 11} y={by + barH / 2 + 3}
                    className="fill-ink text-[12.5px] font-semibold tnum">{short(r.amount)}</text>
              {sum !== 0 && !narrow && (
                <text x={lblW + w + 11} y={by + barH / 2 + 15}
                      className="fill-muted font-mono text-[10.5px] tnum">{pct(r.amount / sum)}</text>
              )}
              <rect x={0} y={y} width={W} height={rowH} fill="transparent" tabIndex={0} role="img"
                    aria-label={`${r.name}: ${short(r.amount)}`}
                    onPointerMove={(e) => setTip({
                      x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, head: r.name,
                      rows: detail ? detail(r) : [['Amount', short(r.amount), c]],
                      foot: sum ? ['Share', pct(r.amount / sum)] : undefined,
                    })}
                    onFocus={() => setTip({ x: lblW + w, y: by, head: r.name,
                      rows: detail ? detail(r) : [['Amount', short(r.amount), c]] })}
                    onPointerLeave={() => setTip(null)}
                    onBlur={() => setTip(null)} />
            </g>
          );
        })}
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

const niceStep = (raw: number) => {
  const mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
};

/** Stacked columns: month × service line. */
export function StackedColumns(
  { months, lines, grid, totals, colourFor }:
  { months: string[]; lines: string[]; grid: Record<string, number[]>; totals: number[];
    colourFor: (name: string) => string },
) {
  const [hover, setHover] = useState<number | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const [box, cw] = useWidth<HTMLDivElement>();
  if (!months.length)
    return <div className="px-5 py-9 text-center text-[13.5px] text-muted">No invoices in this period.</div>;

  // Drawn at its real width, so labels are the size they claim to be and the
  // plot is not letterboxed inside a box of the wrong shape.
  const W = cw > 0 ? Math.round(cw) : 700;
  const narrow = W < 520;
  const H = narrow ? 260 : 330;
  const padL = narrow ? 36 : 52, padR = 12, padT = 26, padB = 32;
  const plotH = H - padT - padB, base = H - padB;
  const peak = Math.max(...totals, 1);
  const step = niceStep(peak / 5);
  const max = Math.ceil(peak / step) * step;
  const scale = plotH / max;
  const band = (W - padL - padR) / months.length;
  const bw = Math.min(24, band * 0.42);

  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);

  return (
    <div className="relative" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} className="w-full block overflow-visible"
           role="img" aria-label="Monthly billings split by service line">
        {ticks.map((v) => {
          const y = base - v * scale;
          return (
            <g key={v}>
              {v > 0 && <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--color-rule-soft)" strokeWidth={1} />}
              <text x={padL - 9} y={y + 3.5} textAnchor="end" className="fill-muted font-mono text-[10.5px] tnum">
                {v === 0 ? '0' : `${(v / 1e7).toFixed(step < 1e7 ? 1 : 0)} L`}
              </text>
            </g>
          );
        })}
        <line x1={padL} y1={base} x2={W - padR} y2={base} stroke="var(--color-baseline)" strokeWidth={1} />

        {months.map((m, i) => {
          const cx = padL + band * i + band / 2;
          const x = cx - bw / 2;
          let acc = 0;
          const stack = lines.map((s) => [s, grid[s]?.[i] ?? 0] as const).filter(([, v]) => v > 0);
          const dim = hover !== null && hover !== i;
          return (
            <g key={m} opacity={dim ? 0.32 : 1} className="transition-opacity">
              {stack.map(([s, v], k) => {
                const rawH = v * scale;
                // A 2px surface gap separates segments; a hairline segment keeps its height.
                const h = rawH > 3.5 ? rawH - 2 : Math.max(rawH, 1);
                const y = base - acc - rawH;
                acc += rawH;
                const top = k === stack.length - 1;
                return <path key={s} d={top ? vBar(x, y, bw, h) : `M${x} ${y} h${bw} v${h} h${-bw} Z`}
                             fill={colourFor(s)} />;
              })}
              <text x={cx} y={base - acc - 9} textAnchor="middle"
                    className="fill-ink text-[12.5px] font-semibold tnum">{short(totals[i] ?? 0)}</text>
              <text x={cx} y={base + 19} textAnchor="middle" className="fill-muted font-mono text-[10.5px]">
                {monthLabel(m).slice(0, 3)}
              </text>
            </g>
          );
        })}

        {/* One hit area per column: a big target, every series in one readout —
            which is also how a 0.1% segment stays reachable. */}
        {months.map((m, i) => {
          const cx = padL + band * i + band / 2;
          return (
            <rect key={m} x={cx - band / 2} y={padT - 16} width={band} height={plotH + padB}
                  fill="transparent" tabIndex={0} role="img"
                  aria-label={`${monthLabel(m)}: ${short(totals[i] ?? 0)} billed`}
                  onPointerMove={(e) => {
                    setHover(i);
                    setTip({
                      x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, head: monthLabel(m),
                      rows: lines.map((s) => [s, short(grid[s]?.[i] ?? 0), colourFor(s)] as [string, string, string])
                        .filter((_, k) => (grid[lines[k]!]?.[i] ?? 0) > 0),
                      foot: ['Billed', short(totals[i] ?? 0)],
                    });
                  }}
                  onFocus={() => setHover(i)}
                  onPointerLeave={() => { setHover(null); setTip(null); }}
                  onBlur={() => { setHover(null); setTip(null); }} />
          );
        })}
      </svg>
      <Tooltip tip={tip} />
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-3.5 border-t border-rule-soft">
        {lines.map((s) => (
          <span key={s} className="flex items-center gap-2 text-xs text-ink-2">
            <i className="w-[11px] h-[11px] rounded-sm shrink-0" style={{ background: colourFor(s) }} />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/** A single part-to-whole bar — receivables ageing. */
export function StackBar({ parts }: { parts: { label: string; amount: number; colour: string }[] }) {
  const total = parts.reduce((a, b) => a + b.amount, 0) || 1;
  return (
    <div className="flex h-2.5 gap-0.5 rounded-full bg-card-3 overflow-hidden">
      {parts.filter((p) => p.amount > 0).map((p) => (
        <span key={p.label} title={`${p.label}: ${short(p.amount)}`}
              style={{ background: p.colour, width: `${(p.amount / total) * 100}%` }} />
      ))}
    </div>
  );
}

export const legendColour = (i: number): string => slotColour(i);
export type { ReactNode };
